import { validatePhone } from '../../../../../shared/utils';
import { z } from 'zod';

const validateABN = (abn: string): boolean => {
    if (typeof abn !== 'string') return false;

    // Remove spaces from the ABN
    abn = abn.replace(/\s/g, '');

    // Check if the ABN is exactly 11 digits
    if (!/^\d{11}$/.test(abn)) {
        return false;
    }

    // Convert to an array of numbers
    const digits = abn.split('').map(Number);

    // Adjust first digit (subtract 1)
    digits[0] -= 1;

    // Weighting factors for ABN validation
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

    // Calculate weighted sum
    const weightedSum = digits.reduce(
        (sum, digit, index) => sum + digit * weights[index],
        0,
    );

    // ABN is valid if sum is divisible by 89
    return weightedSum % 89 === 0;
};

const serviceItems = z.array(z.string().min(1)).nonempty();

/** Signed tokens proving each newly added custom service passed AI validation. */
const serviceValidationTokens = z.array(z.string()).optional();

const CleaningServices = z.object({
    type: z.literal('cleaning'),
    items: serviceItems,
    validationTokens: serviceValidationTokens,
});

const CateringServices = z.object({
    type: z.literal('catering'),
    items: serviceItems,
    validationTokens: serviceValidationTokens,
});

const AlcoholServices = z.object({
    type: z.literal('alcohol'),
    items: serviceItems,
    validationTokens: serviceValidationTokens,
});

const WorkshopServices = z.object({
    type: z.literal('workshop'),
    items: serviceItems,
    validationTokens: serviceValidationTokens,
});

const WellnessServices = z.object({
    type: z.literal('wellness'),
    items: serviceItems,
    validationTokens: serviceValidationTokens,
});

const OtherServices = z.object({
    type: z.literal('other'),
    items: serviceItems,
    validationTokens: serviceValidationTokens,
});

export const ServiceArea = z.object({
    countryCode: z.enum(['AU'], { message: 'Required' }),
    state: z.enum(['NSW', 'QLD', 'TAS', 'VIC', 'ACT', 'WA', 'SA', 'NT'], {
        message: 'Required',
    }),
    areas: z.array(
        z.object({
            name: z.string(),
            googlePlaceId: z.string(),
        }),
    ),
});

export const ContractorProfile = z.object({
    name: z.string().min(1, { message: 'Required' }),
    primaryContact: z.string().min(1, { message: 'Required ' }),
    abn: z.string().refine(validateABN, { message: 'Invalid ABN' }),
    email: z.string().email(),
    services: z
        .discriminatedUnion('type', [
            CleaningServices,
            CateringServices,
            AlcoholServices,
            WorkshopServices,
            WellnessServices,
            OtherServices,
        ])
        .optional(),
    streetAddress: z.string().min(1, { message: 'Required' }),
    suburb: z.string().min(1, { message: 'Required' }),
    state: z.enum(['NSW', 'QLD', 'TAS', 'VIC', 'ACT', 'WA', 'SA', 'NT'], {
        message: 'Required',
    }),
    countryCode: z.enum(['AU'], { message: 'Required' }),
    postcode: z
        .string()
        .min(1, { message: 'Required' })
        .max(4, { message: 'Invalid postcode' }),
    phone: z
        .string()
        .refine(validatePhone, { message: 'Invalid phone number' }),
    serviceStates: z
        .array(ServiceArea)
        .min(1, { message: 'At least 1 service area required' }),
    availableDays: z.array(
        z.object({
            day: z.enum(
                [
                    'monday',
                    'tuesday',
                    'wednesday',
                    'thursday',
                    'friday',
                    'saturday',
                    'sunday',
                ],
                {
                    message: 'Required',
                },
            ),
            periods: z.array(
                z.object({
                    // TODO: Check military time
                    from: z.string(),
                    to: z.string(),
                }),
            ),
        }),
    ),
    unavailablePeriods: z.array(z.object({ from: z.string(), to: z.string() })),
    cancellationPolicy: z.string().optional().nullable(),
    cutoff: z
        .number()
        .min(0, { message: 'Min cutoff is 0 days' })
        .max(7, { message: 'Max cutoff is 7 days' }),
});
